import {useMemo, useState} from "react"
import {usePointsBlendQuery} from "@irati/wallet/hooks/pointsBlend"
import {useWalletCashbackQuery} from "@irati/wallet/hooks/wallet"
import {usePerkOrReward} from "@irati/deals/hooks"
import {PaymentCurrencyType} from "@irati/types"
import {useQuerys, UseQueriesResult} from "@irati/utils/services/apollo"
import {hangPromise} from "@irati/utils/promise"

/**
 * Get the currency and balance for the primary wallet to show (in the header)
 */
export const useHeaderWallet = (): UseQueriesResult<{balance: number; currency: PaymentCurrencyType} | undefined> => {
  const perkOrReward = usePerkOrReward()
  const [fetchCash, setFetchCash] = useState(false)
  const {data, ...apollo} = useQuerys(usePointsBlendQuery(), useWalletCashbackQuery({skip: !fetchCash}))

  return useMemo(() => {
    if (!data) return {...apollo, data: undefined}

    const [flexiWallets, cashWallet] = data

    if (flexiWallets?.walletBlend.visible) {
      const {amount, currency} = flexiWallets.walletBlend
      return {...apollo, data: {currency, balance: amount}}
    }

    // RP has higher precedent in these areas
    if ((perkOrReward.isRecognition || perkOrReward.isRewards) && flexiWallets?.walletRP.visible) {
      const {amount, currency} = flexiWallets.walletRP
      return {...apollo, data: {currency, balance: amount}}
    }

    if (flexiWallets?.walletFP.visible) {
      const {amount, currency} = flexiWallets.walletFP
      return {...apollo, data: {currency, balance: amount}}
    }

    if (flexiWallets?.walletRP.visible) {
      const {amount, currency} = flexiWallets.walletRP
      return {...apollo, data: {currency, balance: amount}}
    }

    // we only want to make the slower cash request if needed, and then wait on the result
    if (!fetchCash) {
      setFetchCash(true)
      return {
        data: undefined,
        loading: true,
        load: hangPromise() as any
      }
    }

    if (cashWallet && cashWallet?.amount > 0) {
      const {amount, currency} = cashWallet
      return {...apollo, data: {currency: currency as PaymentCurrencyType, balance: amount}}
    }

    return {...apollo, data: undefined}
  }, [data, apollo, perkOrReward.isRecognition, perkOrReward.isRewards, fetchCash])
}
